import { type Row, type Table } from '@tanstack/react-table'
import React, { useCallback } from 'react'

import type { VirtualizerType } from '../../Virtualizer/Virtualizer'
import { DataTableCell } from '../DataTableCell/DataTableCell'
import { TableCell } from '../DataTableCell/DataTableCell.styles'
import { TableRow } from '../DataTableCore.styles'

export interface DataTableBodyProps<TData> {
  rowHeight: number
  rowVirtualizer: VirtualizerType
  table: Table<TData>
  withBorder?: boolean
}

export const DataTableVirtualBody = <TData,>({
  rowHeight,
  rowVirtualizer,
  table,
  withBorder,
}: DataTableBodyProps<TData>) => {
  const tableBodyRef = React.useRef<HTMLTableSectionElement>(null)
  const { rows } = table.getRowModel()

  const measureTableRow = useCallback(
    (node: HTMLTableRowElement | null) => rowVirtualizer.measureElement(node),
    [rowVirtualizer]
  )

  return (
    <tbody
      ref={tableBodyRef}
      style={{
        display: 'grid',
        height: `${rowVirtualizer.getTotalSize()}px`,
        position: 'relative',
      }}
    >
      {rowVirtualizer.getVirtualItems().map(virtualRow => {
        const row = rows[virtualRow.index] as Row<TData>

        return (
          <TableRow
            $rowHeight={rowHeight}
            data-e2e="data-table-row"
            data-index={virtualRow.index}
            data-row-disabled={(row.original as TData & { disabled?: boolean }).disabled ?? false}
            key={row.id}
            ref={measureTableRow}
            style={{
              display: 'flex',
              position: 'absolute',
              transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
            }}
          >
            {row.getVisibleCells().map(cell => (
              <DataTableCell
                cell={cell}
                isDisabled={(row.original as TData & { disabled?: boolean }).disabled}
                key={cell.id}
                rowHeight={rowHeight}
                withBorder={withBorder}
              />
            ))}

            <TableCell columnId="last-column" $rowHeight={rowHeight} />
          </TableRow>
        )
      })}
    </tbody>
  )
}
